import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonList, IonItem, IonLabel, IonButton, IonChip, IonSpinner, IonSelect, IonSelectOption,
  IonSegment, IonSegmentButton, IonInput, IonIcon,
} from '@ionic/react';
import { refreshOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { api, apiErrorMessage } from '@/lib/api';
import { toast } from '@/store/ui.store';

type ModerationTab = 'campaigns' | 'posts';

interface AdminCampaign {
  id: string;
  userId: string;
  ownerEmail: string;
  name: string;
  status: string;
  postCount: number;
  createdAt: string;
  updatedAt: string;
}

interface AdminPost {
  id: string;
  userId: string;
  ownerEmail: string;
  title: string | null;
  status: string;
  campaignId: string | null;
  createdAt: string;
}

interface ModerationList<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

const CAMPAIGN_STATUSES = ['draft', 'planning', 'active', 'paused', 'completed', 'failed'];
const POST_STATUSES = ['queued', 'preparing', 'approving', 'scheduled', 'posting', 'posted', 'failed', 'cancelled'];

const statusLabel = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const statusColor = (value: string): 'success' | 'danger' | 'warning' | 'medium' => {
  if (value === 'failed') return 'danger';
  if (value === 'active' || value === 'posted' || value === 'completed') return 'success';
  if (value === 'paused' || value === 'approving' || value === 'cancelled') return 'warning';
  return 'medium';
};

export default function ModerationPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ModerationTab>('campaigns');
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, status: status || undefined, page, limit };
      if (tab === 'campaigns') {
        const { data } = await api.get<ModerationList<AdminCampaign>>('/admin/campaigns', { params });
        setCampaigns(data.data);
        setTotal(data.total);
      } else {
        const { data } = await api.get<ModerationList<AdminPost>>('/admin/posts', { params });
        setPosts(data.data);
        setTotal(data.total);
      }
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [tab, search, status, page]);

  const changeStatus = async (id: string, next: string) => {
    try {
      const path = tab === 'campaigns' ? `/admin/campaigns/${id}` : `/admin/posts/${id}`;
      await api.patch(path, { status: next });
      toast(t('admin.statusUpdated'), 'success');
      await load();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  };

  const switchTab = (next: string) => {
    setTab(next as ModerationTab);
    setPage(1);
    setStatus('');
  };

  const statuses = tab === 'campaigns' ? CAMPAIGN_STATUSES : POST_STATUSES;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('admin.moderation')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void load()}><IonIcon slot="icon-only" icon={refreshOutline} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : (
          <div className="page-shell page-shell--flush">
            <h6 className="section-title">{t('admin.moderationSubtitle')}</h6>

            <IonSegment value={tab} onIonChange={(e) => switchTab(String(e.detail.value))} style={{ marginBottom: 16 }}>
              <IonSegmentButton value="campaigns">
                <IonLabel>{t('admin.campaigns')}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="posts">
                <IonLabel>{t('admin.posts')}</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <IonItem lines="none" detail={false} button={false} style={{ flex: 1, minWidth: 240 }}>
                <IonInput
                  placeholder={t('admin.searchModeration')}
                  value={search}
                  onIonInput={(e) => setSearch(String((e.target as HTMLIonInputElement).value ?? ''))}
                  clearInput
                  slot="start"
                />
              </IonItem>
              <IonSelect
                placeholder={t('admin.allStatuses')}
                value={status}
                onIonChange={(e) => setStatus(e.detail.value as string)}
                interface="popover"
              >
                <IonSelectOption value="">{t('admin.allStatuses')}</IonSelectOption>
                {statuses.map((s) => (
                  <IonSelectOption key={s} value={s}>{statusLabel(s)}</IonSelectOption>
                ))}
              </IonSelect>
            </div>

            <IonList inset>
              {tab === 'campaigns' ? (
                campaigns.length === 0 ? (
                  <IonItem lines="none" detail={false} button={false}>
                    <IonLabel>{t('admin.noResults')}</IonLabel>
                  </IonItem>
                ) : (
                  campaigns.map((c) => (
                    <IonItem key={c.id} lines="full">
                      <IonLabel>
                        <h3>{c.name}</h3>
                        <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>
                          {c.ownerEmail} · {c.postCount} {t('admin.posts').toLowerCase()}
                        </p>
                      </IonLabel>
                      <IonChip color={statusColor(c.status)}>{statusLabel(c.status)}</IonChip>
                      <IonSelect
                        slot="end"
                        interface="popover"
                        value={c.status}
                        aria-label={t('admin.setStatus')}
                        onIonChange={(e) => void changeStatus(c.id, String(e.detail.value))}
                      >
                        {statuses.map((s) => (
                          <IonSelectOption key={s} value={s}>{statusLabel(s)}</IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  ))
                )
              ) : (
                posts.length === 0 ? (
                  <IonItem lines="none" detail={false} button={false}>
                    <IonLabel>{t('admin.noResults')}</IonLabel>
                  </IonItem>
                ) : (
                  posts.map((p) => (
                    <IonItem key={p.id} lines="full">
                      <IonLabel>
                        <h3>{p.title ?? t('admin.untitledPost')}</h3>
                        <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>
                          {p.ownerEmail} · {new Date(p.createdAt).toLocaleDateString()}
                        </p>
                      </IonLabel>
                      <IonChip color={statusColor(p.status)}>{statusLabel(p.status)}</IonChip>
                      <IonSelect
                        slot="end"
                        interface="popover"
                        value={p.status}
                        aria-label={t('admin.setStatus')}
                        onIonChange={(e) => void changeStatus(p.id, String(e.detail.value))}
                      >
                        {statuses.map((s) => (
                          <IonSelectOption key={s} value={s}>{statusLabel(s)}</IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  ))
                )
              )}
            </IonList>

            {total > limit && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginTop: 16 }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--ion-color-medium)' }}>
                  Page {page} of {Math.ceil(total / limit)}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <IonButton size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>
                    {t('common.previous')}
                  </IonButton>
                  <IonButton size="small" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>
                    {t('common.next')}
                  </IonButton>
                </div>
              </div>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}